import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { apiService } from '../services/api';
import { NotificationItem, Notification } from './NotificationItem';
import { useNotificationActions } from '../hooks/useNotificationActions';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useAppContext } from '../contexts/AppContext';
import { openNotificationTarget } from '../navigation/notificationRouter';

const LIMIT = 10;

export interface NotificationsDropdownProps {
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
}

/** Desktop-web notification bell dropdown — a true anchored panel under the
 * header bell icon, replacing full-screen navigation to
 * NotificationHistoryScreen for this breakpoint only (native + mobile-web
 * keep navigating to that screen, matching AvatarMenu's precedent for the
 * account menu). Reuses NotificationItem for rows and
 * useNotificationActions for click-routing/mark-as-read so none of that
 * logic is duplicated a third time. */
export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ unreadCount, onUnreadCountChange }) => {
  const styles = useThemedStyles(getStyles);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<View>(null);
  const { handleNotificationPress } = useNotificationActions(setNotifications);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { navigateTo } = useAppContext();

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await apiService.getNotifications({ limit: LIMIT, offset: 0 });
      if (res.data) {
        setNotifications(res.data.notifications as Notification[]);
        onUnreadCountChange(res.data.unreadCount);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadNotifications();
  }, [open]);

  // Click-outside-to-close — same pattern as AvatarMenu.tsx.
  useEffect(() => {
    if (Platform.OS !== 'web' || !open) return;
    const handleClick = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.authenticatedRequest('/api/notifications/read-all', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      onUnreadCountChange(0);
    } catch (error) {
      console.error('❌ [NotificationsDropdown] Error marking all as read:', error);
    }
  };

  return (
    <View ref={containerRef} style={styles.wrapper}>
      <TouchableOpacity style={styles.iconButton} onPress={() => setOpen((v) => !v)} activeOpacity={0.75}>
        {/* iconWrapper hugs just the icon (not the button's own padding) so
            badge's top/right offsets land relative to the icon itself —
            matching Header.tsx's messages badge exactly, which uses the same
            icon-only wrapper. Without it, the badge anchors to the padded
            button box instead, landing in a visibly different spot even with
            identical offset values. */}
        <View style={styles.iconWrapper}>
          <Ionicons name="notifications-outline" size={24} color={colors.text.primary as string} />
          {unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>

      {open && (
        <View style={[styles.panel, rtl.isRTL ? styles.panelRTL : styles.panelLTR]}>
          <View style={[styles.panelHeader, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.panelTitle, { textAlign: rtl.textStart }]}>{t('notifications.title')}</Text>
            <View style={[styles.headerActions, { flexDirection: rtl.rowDirection }]}>
              {unreadCount > 0 && (
                <TouchableOpacity onPress={handleMarkAllAsRead}>
                  <Text style={styles.markAllText}>{t('notifications.markAllRead')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                onPress={() => { setOpen(false); openNotificationTarget(navigateTo, 'settings:notifications'); }}
                hitSlop={8}
                accessibilityLabel={t('notifications.settings')}
              >
                <Ionicons name="settings-outline" size={18} color={colors.text.secondary as string} />
              </TouchableOpacity>
            </View>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color={colors.brand.primary as string} />
            </View>
          ) : notifications.length === 0 ? (
            <View style={styles.empty}>
              <Ionicons name="notifications-off-outline" size={22} color={colors.text.secondary as string} />
              <Text style={styles.emptyText}>{t('notifications.empty')}</Text>
            </View>
          ) : (
            <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
              {notifications.map((n) => (
                <NotificationItem
                  key={n._id}
                  compact
                  notification={n}
                  onPress={(notification) => {
                    setOpen(false);
                    handleNotificationPress(notification);
                  }}
                />
              ))}
            </ScrollView>
          )}
          <TouchableOpacity style={styles.footer} onPress={() => { setOpen(false); navigateTo('notifications'); }}>
            <Text style={styles.footerText}>{t('notifications.seeAll')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  iconButton: {
    padding: spacing.sm,
    borderRadius: 8,
    backgroundColor: colors.background.secondary,
  },
  iconWrapper: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: colors.error,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
    borderWidth: 2,
    borderColor: colors.brand.chrome,
  },
  badgeText: {
    fontSize: 10,
    color: colors.white,
    fontFamily: typography.fontFamily.bold,
    textAlign: 'center',
  },
  panel: {
    position: 'absolute',
    top: '100%',
    marginTop: spacing.xs,
    width: 360,
    maxHeight: 480,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    ...designTokens.shadows.lg,
    zIndex: 2000,
  },
  // Anchor to whichever edge keeps the panel on-screen: in LTR the bell
  // sits near the header's right edge, so the panel hangs from its right
  // (right: 0); in RTL the bell is mirrored to the header's left edge, so
  // right: 0 would push a 360px panel off-screen — anchor from the left
  // instead.
  panelLTR: {
    right: 0,
  },
  panelRTL: {
    left: 0,
  },
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  panelTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  headerActions: { alignItems: 'center', gap: spacing.md },
  footer: { paddingVertical: spacing.sm + 2, alignItems: 'center', borderTopWidth: 1, borderTopColor: colors.border },
  footerText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.brand.primary },
  markAllText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.brand.primary,
  },
  scroll: {
    maxHeight: 420,
  },
  loadingContainer: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xl,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
